/**
 * Inline icons, drawn to the mockup's 24px grid and inheriting `currentColor`
 * so a look's accent applies without any of them knowing about colour.
 */

type IconProps = { className?: string };

const base = {
  // An SVG with a viewBox but no width/height fills its container, which is how
  // the search magnifier ended up the size of the window. CSS can still
  // override these; nothing should have to.
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

export function MailIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </svg>
  );
}

export function FilesIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h4l2 2.5h7A1.5 1.5 0 0 1 20 9v8.5A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5Z" />
    </svg>
  );
}

export function MeetIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <rect x="3" y="6" width="12" height="12" rx="2" />
      <path d="m15 11 5-3v8l-5-3z" />
    </svg>
  );
}

export function ChatIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M20 12a7 7 0 0 1-7 7H8l-4 3v-4.5A7 7 0 0 1 11 5h2a7 7 0 0 1 7 7Z" />
    </svg>
  );
}

export function InboxIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M4 13 6 5h12l2 8v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
      <path d="M4 13h4l1 2.5h6L16 13h4" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.5-4.5" />
    </svg>
  );
}

export function CalendarIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </svg>
  );
}

export function ContactsIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  );
}

type Paths = (
  string | { circle: [number, number, number] } | { rect: [number, number, number, number, number] }
)[];

/** One icon from a list of paths, drawn on the same grid and stroke as the rest. */
function icon(paths: Paths) {
  return function Icon({ className }: IconProps): JSX.Element {
    return (
      <svg {...base} className={className}>
        {paths.map((p, i) =>
          typeof p === 'string' ? (
            <path key={i} d={p} />
          ) : 'circle' in p ? (
            <circle key={i} cx={p.circle[0]} cy={p.circle[1]} r={p.circle[2]} />
          ) : (
            <rect
              key={i}
              x={p.rect[0]}
              y={p.rect[1]}
              width={p.rect[2]}
              height={p.rect[3]}
              rx={p.rect[4]}
            />
          ),
        )}
      </svg>
    );
  };
}

export const ArchiveIcon = icon([
  { rect: [3.5, 4, 17, 4.5, 1.2] },
  'M5 8.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5',
  'M10 12.5h4',
]);
export const TrashIcon = icon([
  'M4.5 7h15',
  'M9.5 7V5h5v2',
  'M6.5 7l.8 11.2A2 2 0 0 0 9.3 20h5.4a2 2 0 0 0 2-1.8L17.5 7',
  'M10.5 11v5M13.5 11v5',
]);
export const FlagIcon = icon(['M6 21V4', 'M6 4.5h10.5l-2 4 2 4H6']);
export const ReplyIcon = icon(['M10 6 4.5 11.5 10 17', 'M5 11.5h8.5a6 6 0 0 1 6 6V19']);
export const ReplyAllIcon = icon([
  'M8 6 2.5 11.5 8 17',
  'M12.5 6 7 11.5l5.5 5.5',
  'M7.5 11.5h6a6 6 0 0 1 6 6V19',
]);
export const ForwardIcon = icon(['M14 6l5.5 5.5L14 17', 'M19 11.5h-8.5a6 6 0 0 0-6 6V19']);
export const PaperclipIcon = icon([
  'M20 11.5 12 19.5a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L9.9 17a1.7 1.7 0 0 1-2.4-2.4L15 7',
]);
export const MoreIcon = icon([
  { circle: [5.5, 12, 1] },
  { circle: [12, 12, 1] },
  { circle: [18.5, 12, 1] },
]);
export const RefreshIcon = icon(['M19.5 12a7.5 7.5 0 1 1-2.2-5.3', 'M19.5 4.5v4h-4']);
export const CloseIcon = icon(['M6 6l12 12M18 6 6 18']);
/** Back to the list, on a phone. */
export const BackIcon = icon(['M15 5l-7 7 7 7']);
/** The folders drawer, on a phone. */
export const MenuIcon = icon(['M4 7h16M4 12h16M4 17h16']);
export const CheckIcon = icon(['M5 12.5l4.5 4.5L19 7.5']);
export const ChevronLeftIcon = icon(['M14.5 6 8.5 12l6 6']);
export const ChevronRightIcon = icon(['M9.5 6l6 6-6 6']);
export const PenIcon = icon(['M4 20l1-4.5L15.5 5a2.1 2.1 0 0 1 3 3L8 18.5Z', 'M13.5 7l3 3']);
export const EnvelopeOpenIcon = icon([
  'M3.5 10 12 4.5 20.5 10v8.5a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z',
  'm3.5 10 8.5 5.5 8.5-5.5',
]);
export const ShieldIcon = icon([
  'M12 3.5 5 6v5.5c0 4.2 2.9 7.5 7 9 4.1-1.5 7-4.8 7-9V6Z',
  'M12 9v4M12 16h.01',
]);
export const FolderMoveIcon = icon([
  'M3.5 6.5A1.5 1.5 0 0 1 5 5h4l2 2.5h8A1.5 1.5 0 0 1 20.5 9v8.5A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5Z',
  'M10 13h5m-2-2 2 2-2 2',
]);
export const BoldIcon = icon(['M7 5h6a3.5 3.5 0 0 1 0 7H7Z', 'M7 12h7a3.5 3.5 0 0 1 0 7H7Z']);
export const ItalicIcon = icon(['M10 5h8M6 19h8M14.5 5l-5 14']);
export const UnderlineIcon = icon(['M7 4.5V11a5 5 0 0 0 10 0V4.5', 'M5.5 20h13']);
export const ListIcon = icon([
  'M9.5 7h10M9.5 12h10M9.5 17h10',
  { circle: [5, 7, 0.6] },
  { circle: [5, 12, 0.6] },
  { circle: [5, 17, 0.6] },
]);
export const ListNumberIcon = icon([
  'M10 7h10M10 12h10M10 17h10',
  'M4.5 5.5 6 5v4',
  'M4.5 14.5c.4-.6 2.6-.7 2.1.8L4.5 18h2.3',
]);
export const LinkIcon = icon([
  'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1',
  'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
]);
export const QuoteIcon = icon([
  'M6 10h4v5H6zM6 10c0-2.5 1.2-4 3.5-4.5M14 10h4v5h-4zM14 10c0-2.5 1.2-4 3.5-4.5',
]);
export const SendIcon = icon(['M4 12 20 4.5l-4.5 16-3.5-7Z', 'M12 13.5 20 4.5']);
export const ClockIcon = icon([{ circle: [12, 12, 8] }, 'M12 7.5V12l3 2']);
export const UsersIcon = icon([
  { circle: [9, 9, 3] },
  'M3.5 19a5.5 5.5 0 0 1 11 0',
  'M15.5 6.2a3 3 0 0 1 0 5.6M17 14a5.5 5.5 0 0 1 3.5 5',
]);
export const PinIcon = icon([
  'M12 21s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11Z',
  { circle: [12, 10, 2.3] },
]);
export const BellIcon = icon([
  'M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 1.5h-15Z',
  'M10 20.5a2 2 0 0 0 4 0',
]);
export const RepeatIcon = icon([
  'M17 3.5l2.5 2.5L17 8.5',
  'M4.5 11.5V10A4 4 0 0 1 8.5 6h11',
  'M7 20.5 4.5 18 7 15.5',
  'M19.5 12.5V14a4 4 0 0 1-4 4h-11',
]);
export const MinimizeIcon = icon(['M5 18h14']);
export const ExpandIcon = icon(['M14 4.5h5.5V10M10 19.5H4.5V14M19.5 4.5 13.5 10.5M4.5 19.5l6-6']);
export const KeyboardIcon = icon([
  { rect: [2.5, 6, 19, 12, 2] },
  'M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7.5 14h9',
]);
export const StarIcon = icon([
  'M12 4l2.4 5 5.4.6-4 3.7 1.1 5.4L12 16l-4.9 2.7 1.1-5.4-4-3.7 5.4-.6Z',
]);
export const PlusIcon = icon(['M12 5v14M5 12h14']);
/** The writing assistant's mark, from the mockup's composer strip. */
export const SparkIcon = icon([
  'M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9z',
  'M18.5 3v3M20 4.5h-3',
]);
/** A message's source: its raw headers and parts. */
export const CodeIcon = icon(['M9 7l-5 5 5 5', 'M15 7l5 5-5 5']);
export const DocumentIcon = icon([
  'M7 3.5h6.5L18 8v12a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 7 20Z',
  'M13 3.5V8h5',
]);
export const UploadIcon = icon([
  'M12 15V4.5',
  'm7.5 9 4.5-4.5L16.5 9',
  'M5 15.5v3A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-3',
]);
export const DownloadIcon = icon([
  'M12 4.5V15',
  'm7.5 10.5 4.5 4.5 4.5-4.5',
  'M5 15.5v3A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-3',
]);
export const GridIcon = icon([
  { rect: [4, 4, 7, 7, 1.5] },
  { rect: [13, 4, 7, 7, 1.5] },
  { rect: [4, 13, 7, 7, 1.5] },
  { rect: [13, 13, 7, 7, 1.5] },
]);
export const BuildingIcon = icon([
  'M5 20V5.5A1.5 1.5 0 0 1 6.5 4h7A1.5 1.5 0 0 1 15 5.5V20',
  'M15 10h3.5a1.5 1.5 0 0 1 1.5 1.5V20',
  'M3.5 20h17M8.5 8h3M8.5 11.5h3M8.5 15h3',
]);
export const ServerIcon = icon([
  { rect: [4, 4.5, 16, 6, 1.5] },
  { rect: [4, 13.5, 16, 6, 1.5] },
  'M7.5 7.5h.01M7.5 16.5h.01M11 7.5h5.5M11 16.5h5.5',
]);
export const GearIcon = icon([
  { circle: [12, 12, 3] },
  'M12 2.5v2.5M12 19v2.5M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M2.5 12H5M19 12h2.5M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8',
]);
